import React, { useContext, useState } from "react";
import cx from "classnames";
import styles from "../../scss/components/DapProceedDialog.module.scss";
import LModal from "../../../common/components/Modals/LModal/LModal";
import Checkbox from "../../../common/components/Forms/Checkbox/Checkbox";
import Button from "../../../common/components/Forms/Button/Button";
import { Form, Formik } from "formik";
import Input from "../../../common/components/Forms/Input/Input";
import * as Yup from "yup";
import { AdviceControllerContext } from "../../providers/DigitalAdviceProvider";
import {
    setQuestionAnswer,
    setRecommendationDisclaimer
} from "../../api/ApiDigitalAdvice";
import useErrorLogger from "../../../common/hooks/useErrorLogger";

interface IDapProceedDialog {
    isOpen: boolean;
    hasExistingInsuranceType?: boolean;
    onClose: () => void;
}

const DapProceedDialog: React.FC<IDapProceedDialog> = (props) => {
    const { isOpen, onClose, hasExistingInsuranceType } = props;
    const [hasAcceptedSuitability, setHasAcceptedSuitability] = useState(false);
    const [hasAcceptedReplacingCover, setHasAcceptedReplacingCover] = useState(false);
    const { selectors } = useContext(AdviceControllerContext);

    const intialValues = {
        email: ""
    };

    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email("Please enter a valid email")
            .required("Please provide a contact email")
    });

    const onSubmit = (data: typeof intialValues) => {
        setRecommendationDisclaimer({
            sessionUserId: selectors.getSessionUserId(),
            understoodSuitability: true,
            understoodReplacement: true,
            recommendationEmail: data.email,
            isDone: true
        })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => onClose());
    };

    return (
        <LModal
            Header={() => null}
            calcHeightMode="auto"
            width="wide"
            removePadding={true}
            isOpen={isOpen}>
            {() => {
                return (
                    <div className={cx(styles["dap-proceed-dialog-modal"])}>
                        <div className={styles["dap-proceed__header"]}>
                            <h2>Before you proceed</h2>
                        </div>
                        <div className={styles["dap-proceed__content"]}>
                            <Checkbox
                                name="suitability"
                                size="l"
                                className={cx(styles["dap-proceed__checkbox"], "gap-xl")}
                                checked={hasAcceptedSuitability}
                                onChange={(e) =>
                                    setHasAcceptedSuitability(e.target.checked)
                                }
                                label={
                                    <span
                                        className={cx(
                                            "space-pre-m",
                                            styles["dap-proceed__checkbox-span"]
                                        )}>
                                        I understand that the suitability of the cover
                                        being recommended is based on my stated goals from
                                        the start of this advice tool.
                                    </span>
                                }
                            />

                            {hasExistingInsuranceType && (
                                <Checkbox
                                    name="replacing-cover"
                                    size="l"
                                    className={cx(
                                        styles["dap-proceed__checkbox"],
                                        "gap-xl"
                                    )}
                                    checked={hasAcceptedReplacingCover}
                                    onChange={(e) =>
                                        setHasAcceptedReplacingCover(e.target.checked)
                                    }
                                    label={
                                        <span
                                            className={cx(
                                                "space-pre-m",
                                                styles["dap-proceed__checkbox-span"]
                                            )}>
                                            I understand that when replacing existing
                                            cover, it's important to understand the
                                            difference your replacement cover will be from
                                            your current one.
                                        </span>
                                    }
                                />
                            )}

                            <p className={styles["dap-proceed__note"]}>
                                If you have any questions or concerns around this, please{" "}
                                <a href="/our-service/contact-us.aspx" target="_blank">
                                    contact us
                                </a>{" "}
                            </p>

                            <Formik
                                initialValues={intialValues}
                                onSubmit={onSubmit}
                                validationSchema={validationSchema}>
                                {({
                                    values,
                                    errors,
                                    touched,
                                    handleChange,
                                    handleBlur,
                                    isSubmitting
                                }) => (
                                    <Form
                                        className={cx(
                                            styles["dap-proceed__form"],
                                            "layout-flex-column--centered"
                                        )}>
                                        <Input
                                            name={"email"}
                                            label={`Email address`}
                                            type={`email`}
                                            value={values.email}
                                            placeholder="sample@email.com"
                                            className="width-full"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={
                                                touched.email && errors.email
                                            }></Input>

                                        <p className={styles["dap-proceed__email-note"]}>
                                            We'll email you a copy of your recommendations{" "}
                                            {!!(touched.email && errors.email)}
                                        </p>

                                        <Button
                                            className={styles["dap-proceed__continue"]}
                                            width="content"
                                            component="button"
                                            size="s"
                                            type="submit"
                                            isDisabled={
                                                !hasAcceptedSuitability ||
                                                (hasExistingInsuranceType &&
                                                    !hasAcceptedReplacingCover) ||
                                                !!errors.email ||
                                                !values.email ||
                                                isSubmitting
                                            }
                                            variant="calltoaction">
                                            <span>Continue</span>
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                );
            }}
        </LModal>
    );
};

export default DapProceedDialog;
